import React, { useMemo, useState, useCallback, createContext, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import {
  ThemeProvider,
  createTheme,
  CssBaseline
} from '@mui/material';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// Layout & Pages
import DashboardLayout from './components/DashboardLayout';
import HomePage from './pages/HomePage';
import StudentsPage from './pages/StudentsPage';
import TeachersPage from './pages/TeachersPage';
import ClassesPage from './pages/ClassesPage';
import AssignmentsPage from './pages/AssignmentsPage';
import GradesPage from './pages/GradesPage';
import EnrollmentPage from './pages/EnrollmentPage';
import ParentsPage from './pages/ParentsPage';
import AnnouncementsPage from './pages/AnnouncementsPage';
import StudentAttendancePage from './pages/StudentAttendancePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ExercisesPage from './pages/ExercisesPage';
import NotificationsPage from './pages/NotificationsPage';
import NotFoundPage from './pages/NotFoundPage';
import CalendarPage from './pages/CalendarPage';
import StudentPromotionPage from './pages/StudentPromotionPage';
import TeacherAttendancePage from './pages/TeacherAttendancePage';
import FeedingFeeConfig from './pages/FeedingFeeConfig';
import FeedingFeePage from './pages/FeedingFeePage';
import ClassFeeConfigPage from './pages/ClassFeeConfigPage';
import ExpenseProfitPage from './pages/ExpenseProfitPage';
import TermBillingPage from './pages/TermBillingPage';
import SchoolInfoPage from './pages/SchoolInfoPage';
import TermsAndConditionsPage from './pages/TermsAndConditionsPage';
import SmsManagementPage from './pages/SmsManagementPage';

// ✅ NEW: Admin Reset Requests Page
import AdminResetRequestsPage from './pages/AdminResetRequestsPage';
import AdminManagementPage from './pages/AdminManagementPage';
import SuperAdminRegisterPage from './pages/SuperAdminRegisterPage';
import SuperAdminLoginPage from './pages/SuperAdminLoginPage';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import SchoolRecordsPage from './pages/SchoolRecordsPage';
import SchoolLogbookPage from './pages/SchoolLogbookPage';
import ReleaseNotesPage from './pages/ReleaseNotesPage';
import SbaV2AdminPage from './pages/SbaV2AdminPage';
import SchoolBillingPage from './pages/SchoolBillingPage';
import SchoolAdmissionRegisterPage from './pages/SchoolAdmissionRegisterPage'; // ✅ NEW: Admission Register
import IdleTimer from './components/IdleTimer';
import TeacherPortalLogin from './pages/teacherPortal/TeacherPortalLogin';
import TeacherPortalLayout from './pages/teacherPortal/TeacherPortalLayout';
import TeacherPortalHome from './pages/teacherPortal/TeacherPortalHome';
import TeacherClockScreen from './pages/teacherPortal/TeacherClockScreen';
import TeacherStudentAttendanceScreen from './pages/teacherPortal/TeacherStudentAttendanceScreen';
import TeacherExamsReportScreen from './pages/teacherPortal/TeacherExamsReportScreen';
import TeacherFeedingFeeScreen from './pages/teacherPortal/TeacherFeedingFeeScreen';
import TeacherAssignmentsScreen from './pages/teacherPortal/TeacherAssignmentsScreen';
import TeacherAnnouncementsScreen from './pages/teacherPortal/TeacherAnnouncementsScreen';
import TeacherTimetableScreen from './pages/teacherPortal/TeacherTimetableScreen';
import TeacherExercisesScreen from './pages/teacherPortal/TeacherExercisesScreen';

// Transport Module Pages
import TransportRoutesPage from './pages/TransportRoutesPage';
import TransportEnrollmentPage from './pages/TransportEnrollmentPage';
import TransportAssignmentPage from './pages/TransportAssignmentPage';
import TransportFeesPage from './pages/TransportFeesPage';
import TransportBusesPage from './pages/TransportBusesPage';
import TransportReportsPage from './pages/TransportReportsPage';
import TransportDashboard from './pages/TransportDashboard';
import TransportClassBoardingPage from './pages/TransportClassBoardingPage';

// Payroll Module Pages
import PayrollSettingsPage from './pages/PayrollSettingsPage';
import TeacherSalaryPage from './pages/TeacherSalaryPage';
import PayrollProcessingPage from './pages/PayrollProcessingPage';
import { AuthProvider } from './contexts/AuthContext';
import { getDefaultDashboardPath, hasPermission } from './utils/permissions';

// Theme toggle context
export const ColorModeContext = createContext({ toggleColorMode: () => { } });

// 🔒 Simple Protected Route wrapper for School Admins
const SCHOOL_ADMIN_ROLES = ['admin', 'school-admin', 'school_admin', 'schoolAdmin'];

const ProtectedRoute = ({ children }) => {
  const userStr = localStorage.getItem('user');
  if (!userStr) return <Navigate to="/login" replace />;
  const user = JSON.parse(userStr);
  // Optional: Prevent superadmin from accessing school dashboard if desired
  if (user.role === 'superadmin') return <Navigate to="/super-admin/dashboard" replace />;
  if (!SCHOOL_ADMIN_ROLES.includes(user.role)) {
    if (user.role === 'teacher') return <Navigate to="/teacher-portal" replace />;
    return <Navigate to="/login" replace />;
  }
  return children;
};

const TeacherPortalRoute = ({ children }) => {
  const userStr = localStorage.getItem('user');
  if (!userStr) return <Navigate to="/teacher-portal/login" replace />;

  const user = JSON.parse(userStr);
  if (user.role !== 'teacher') {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// 🛡️ Super Admin Protection Wrapper
const SuperAdminRoute = ({ children }) => {
  const userStr = localStorage.getItem('user');
  if (!userStr) return <Navigate to="/super-admin/login" replace />;

  const user = JSON.parse(userStr);
  if (user.role !== 'superadmin') {
    return <Navigate to="/login" replace />;
  }

  return children;
};

const AccessDenied = () => (
  <div style={{ padding: 32 }}>
    <h2 style={{ marginTop: 0 }}>Access denied</h2>
    <p>You are not allowed to perform this action</p>
  </div>
);

const PermissionRoute = ({ permission, children }) => {
  const userStr = localStorage.getItem('user');
  if (!userStr) return <Navigate to="/login" replace />;

  const user = JSON.parse(userStr);
  if (!hasPermission(permission, user)) {
    return <AccessDenied />;
  }

  return children;
};

const DashboardLanding = () => {
  const userStr = localStorage.getItem('user');
  if (!userStr) return <Navigate to="/login" replace />;

  const user = JSON.parse(userStr);
  if (hasPermission('canViewDashboard', user)) {
    return <HomePage />;
  }

  const defaultPath = getDefaultDashboardPath(user);
  if (defaultPath && defaultPath !== '/dashboard') {
    return <Navigate to={defaultPath} replace />;
  }

  return <AccessDenied />;
};

export default function App() {
  // Use localStorage to persist theme, default to 'dark' for modern SaaS feel
  const [mode, setMode] = useState(() => {
    const savedMode = localStorage.getItem('themeMode');
    return savedMode ? savedMode : 'light';
  });

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prev) => {
          const newMode = prev === 'light' ? 'dark' : 'light';
          localStorage.setItem('themeMode', newMode);
          return newMode;
        });
      },
    }),
    []
  );

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          ...(mode === 'light'
            ? {
                // Polished modern light theme equivalent
                primary: { main: '#3b82f6' },
                success: { main: '#22c55e' },
                warning: { main: '#f59e0b' },
                error: { main: '#ef4444' },
                background: {
                  default: '#f8fafc',
                  paper: '#ffffff',
                },
                text: {
                  primary: '#0f172a',
                  secondary: '#64748b',
                },
                divider: '#e2e8f0',
                action: {
                  active: '#64748b',
                  hover: '#f1f5f9',
                  selected: '#e2e8f0',
                },
              }
            : {
                // Modern, layered dark theme requested by user
                primary: { main: '#3b82f6' },
                success: { main: '#22c55e' },
                warning: { main: '#f59e0b' },
                error: { main: '#ef4444' },
                background: {
                  default: '#0f172a', // deep background
                  paper: '#1e293b',   // layered surface
                },
                text: {
                  primary: '#e2e8f0', // bright readable text
                  secondary: '#cbd5e1', // clearer secondary text
                },
                divider: '#2e3a4f',
                action: {
                  active: '#94a3b8',
                  hover: '#334155',
                  selected: 'rgba(59, 130, 246, 0.16)', // subtle primary tint
                  disabled: 'rgba(226, 232, 240, 0.3)',
                  disabledBackground: 'rgba(226, 232, 240, 0.12)',
                },
              }),
        },
        typography: {
          fontFamily: 'Roboto, Poppins, Inter, sans-serif',
          h1: { fontWeight: 600, color: mode === 'dark' ? '#e2e8f0' : '#0f172a' },
          h2: { fontWeight: 600, color: mode === 'dark' ? '#e2e8f0' : '#0f172a' },
          h3: { fontWeight: 600, color: mode === 'dark' ? '#e2e8f0' : '#0f172a' },
          h4: { fontWeight: 600, color: mode === 'dark' ? '#e2e8f0' : '#0f172a' },
          h5: { fontWeight: 600, color: mode === 'dark' ? '#e2e8f0' : '#0f172a' },
          h6: { fontWeight: 600, color: mode === 'dark' ? '#e2e8f0' : '#0f172a' },
          subtitle1: { color: mode === 'dark' ? '#e2e8f0' : '#0f172a' },
          body2: { color: mode === 'dark' ? '#e2e8f0' : '#0f172a' },
        },
        shape: {
          borderRadius: 12, // Modern SaaS rounded corners
        },
        components: {
          MuiButton: {
            styleOverrides: {
              root: {
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 8,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-1px)',
                }
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: 'none',
                transition: 'box-shadow 0.3s ease-in-out, background-color 0.3s ease-in-out',
                boxShadow: mode === 'dark' 
                  ? '0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)' 
                  : '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                border: mode === 'dark' ? '1px solid #2e3a4f' : '1px solid #e2e8f0',
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                backgroundImage: 'none',
                borderRadius: 12,
                transition: 'transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease',
                boxShadow: mode === 'dark' 
                  ? '0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)' 
                  : '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                border: mode === 'dark' ? '1px solid #2e3a4f' : '1px solid #e2e8f0',
                '&:hover': {
                  boxShadow: mode === 'dark' 
                     ? '0 10px 15px -3px rgba(0, 0, 0, 0.4), 0 4px 6px -2px rgba(0, 0, 0, 0.25)' 
                     : '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                }
              }
            }
          },
          MuiAppBar: {
            styleOverrides: {
              root: {
                backgroundColor: mode === 'dark' ? '#1e293b' : '#ffffff',
                color: mode === 'dark' ? '#e2e8f0' : '#0f172a',
                boxShadow: 'none',
                borderBottom: mode === 'dark' ? '1px solid #2e3a4f' : '1px solid #e2e8f0',
              }
            }
          },
          MuiDrawer: {
            styleOverrides: {
              paper: {
                backgroundColor: mode === 'dark' ? '#0f172a' : '#f8fafc',
                borderRight: mode === 'dark' ? '1px solid #2e3a4f' : '1px solid #e2e8f0',
              }
            }
          },
          MuiTableCell: {
            styleOverrides: {
              root: {
                borderBottom: mode === 'dark' ? '1px solid #2e3a4f' : '1px solid #e2e8f0',
              },
              head: {
                color: mode === 'dark' ? '#94a3b8' : '#64748b',
                fontWeight: 600,
              }
            }
          },
          MuiCssBaseline: {
            styleOverrides: `
              body {
                scrollbar-width: thin;
                scrollbar-color: ${mode === 'dark' ? '#334155 #0f172a' : '#cbd5e1 #f8fafc'};
              }
              body::-webkit-scrollbar {
                width: 8px;
                height: 8px;
              }
              body::-webkit-scrollbar-track {
                background: ${mode === 'dark' ? '#0f172a' : '#f8fafc'};
              }
              body::-webkit-scrollbar-thumb {
                background-color: ${mode === 'dark' ? '#334155' : '#cbd5e1'};
                border-radius: 4px;
              }
              body::-webkit-scrollbar-thumb:hover {
                background-color: ${mode === 'dark' ? '#475569' : '#94a3b8'};
              }
            `,
          },
        },
      }),
    [mode]
  );

  useEffect(() => {
    const browserChromeColor = mode === 'dark' ? '#1e293b' : '#ffffff';
    const pageBackgroundColor = mode === 'dark' ? '#0f172a' : '#ffffff';
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');

    if (themeColorMeta) {
      themeColorMeta.setAttribute('content', browserChromeColor);
    }

    document.documentElement.style.backgroundColor = pageBackgroundColor;
    document.body.style.backgroundColor = pageBackgroundColor;
  }, [mode]);

  const handleLogout = useCallback(() => {
    // Only logout if user is actually logged in
    const userStr = localStorage.getItem('user');
    if (userStr) {
      let user = null;
      try {
        user = JSON.parse(userStr);
      } catch {
        user = null;
      }
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      window.location.href = user?.role === 'teacher' ? '/teacher-portal/login' : '/login';
    }
  }, []);

  return (
    <ColorModeContext.Provider value={colorMode}>
      <AuthProvider>
        <ThemeProvider theme={theme}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <CssBaseline />
            <Router>
            <IdleTimer timeout={300000} onIdle={handleLogout} />
            <ToastContainer position="top-right" autoClose={5000} />
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/terms" element={<TermsAndConditionsPage />} />
              <Route path="/teacher-portal/login" element={<TeacherPortalLogin />} />

              <Route
                path="/teacher-portal"
                element={
                  <TeacherPortalRoute>
                    <TeacherPortalLayout />
                  </TeacherPortalRoute>
                }
              >
                <Route index element={<TeacherPortalHome />} />
                <Route path="clock" element={<TeacherClockScreen />} />
                <Route path="student-attendance" element={<TeacherStudentAttendanceScreen />} />
                <Route path="exams-report" element={<TeacherExamsReportScreen />} />
                <Route path="feeding-fee" element={<TeacherFeedingFeeScreen />} />
                <Route path="assignments" element={<TeacherAssignmentsScreen />} />
                <Route path="announcements" element={<TeacherAnnouncementsScreen />} />
                <Route path="timetable" element={<TeacherTimetableScreen />} />
                <Route path="exercises" element={<TeacherExercisesScreen />} />
              </Route>

              {/* 🛡️ Super Admin Public Routes */}
              <Route path="/super-admin/login" element={<SuperAdminLoginPage />} />
              <Route path="/super-admin/register" element={<SuperAdminRegisterPage />} />

              {/* 🛡️ Super Admin Protected Dashboard */}
              <Route
                path="/super-admin/dashboard"
                element={
                  <SuperAdminRoute>
                    <SuperAdminDashboard />
                  </SuperAdminRoute>
                }
              />
              <Route
                path="/super-admin/release-notes"
                element={
                  <SuperAdminRoute>
                    <ReleaseNotesPage superAdminMode />
                  </SuperAdminRoute>
                }
              />

              {/* Redirect root to /login */}
              <Route path="/" element={<Navigate to="/login" replace />} />

              {/* Protected Dashboard Routes (School Admin) */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <DashboardLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<DashboardLanding />} />
                <Route path="students" element={<StudentsPage />} />
                <Route path="teachers" element={<PermissionRoute permission="canViewTeachers"><TeachersPage /></PermissionRoute>} />
                <Route path="classes" element={<ClassesPage />} />
                <Route path="assignments" element={<AssignmentsPage />} />
                <Route path="grades" element={<PermissionRoute permission="canExportReports"><GradesPage /></PermissionRoute>} />
                <Route path="enrollment" element={<EnrollmentPage />} />
                <Route path="parents" element={<ParentsPage />} />
                <Route path="announcements" element={<AnnouncementsPage />} />
                <Route path="attendance" element={<PermissionRoute permission="canViewAttendance"><StudentAttendancePage /></PermissionRoute>} />
                <Route path="exercises" element={<ExercisesPage />} />
                <Route path="notifications" element={<NotificationsPage />} />
                <Route path="calendar" element={<CalendarPage />} />
                <Route path="students/promote" element={<StudentPromotionPage />} />
                <Route path="teacher/attendance" element={<PermissionRoute permission="canViewAttendance"><TeacherAttendancePage /></PermissionRoute>} />
                <Route path="feeding-config" element={<PermissionRoute permission="canEditFeedingFee"><FeedingFeeConfig /></PermissionRoute>} />
                <Route path="feeding-fees" element={<PermissionRoute permission="canViewFeedingFee"><FeedingFeePage /></PermissionRoute>} />
                <Route path="class-fees" element={<PermissionRoute permission="canEditFees"><ClassFeeConfigPage /></PermissionRoute>} />
                <Route path="expense-profit" element={<PermissionRoute permission="canViewFees"><ExpenseProfitPage /></PermissionRoute>} />
                <Route path="fees" element={<PermissionRoute permission="canViewFees"><TermBillingPage /></PermissionRoute>} />
                <Route path="sms" element={<SmsManagementPage />} />
                <Route path="school-info" element={<SchoolInfoPage />} />
                <Route path="sba-records" element={<PermissionRoute permission="canExportReports"><SbaV2AdminPage /></PermissionRoute>} />

                {/* ✅ NEW: Admin Password Reset Requests */}
                <Route path="reset-requests" element={<PermissionRoute permission="canManageAdmins"><AdminResetRequestsPage /></PermissionRoute>} />
                <Route path="admins" element={<PermissionRoute permission="canManageAdmins"><AdminManagementPage /></PermissionRoute>} />
                {/* 📊 NEW: School Records Management */}
                <Route path="school-records" element={<SchoolRecordsPage />} />
                <Route path="logbook" element={<SchoolLogbookPage />} />
                {/* 📖 NEW: Admission Register */}
                <Route path="admission-register" element={<SchoolAdmissionRegisterPage />} />
                {/* 💳 NEW: School Billing Page */}
                <Route path="platform-billing" element={<SchoolBillingPage />} />

                {/* 🚌 Transport Management */}
                <Route path="transport/dashboard" element={<TransportDashboard />} />
                <Route path="transport/routes" element={<TransportRoutesPage />} />
                <Route path="transport/enrollment" element={<TransportEnrollmentPage />} />
                <Route path="transport/class-boarding" element={<TransportClassBoardingPage />} />
                <Route path="transport/assignments" element={<TransportAssignmentPage />} />
                <Route path="transport/fees" element={<TransportFeesPage />} />
                <Route path="transport/buses" element={<TransportBusesPage />} />
                <Route path="transport/reports" element={<TransportReportsPage />} />

                {/* 💰 Payroll Management */}
                <Route path="payroll/salaries" element={<TeacherSalaryPage />} />
                <Route path="payroll/settings" element={<PayrollSettingsPage />} />
                <Route path="payroll/processing" element={<PayrollProcessingPage />} />
              </Route>

              {/* 404 Catch-All */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            </Router>
          </LocalizationProvider>
        </ThemeProvider>
      </AuthProvider>
    </ColorModeContext.Provider>
  );
}
